import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';
import { DialogService } from '../../dialog/dialog.service';
import { dayAsString, monthAsShortString } from '../../shared/date.helper';
import { Inspection } from '../../shared/interfaces';
import { InspectionAssignInspectorComponent } from '../assign.inspector/assign.inspector.component';
import { InspectionService } from '../inspection.service';
import { InspectionStatus } from '../inspection.status';
import { AnswerStatus } from '../answer.status';

@Component({
  selector: 'app-inspection-vendor-flow',
  templateUrl: './inspection-vendor-flow.component.html',
  styleUrls: ['./inspection-vendor-flow.component.scss'],
})
export class InspectionVendorFlowComponent implements OnInit {
  @ViewChild('assignInspectorModal', { static: true }) public assignInspectorModal: InspectionAssignInspectorComponent;

  pending: Inspection[] = [];
  ready: Inspection[] = [];
  rejected: Inspection[] = [];
  noInspector: Inspection[] = [];

  loadDebounced = new Subject();
  public AnswerStatus = AnswerStatus;
  answers: any[];

  constructor(
    private toastr: ToastrService,
    public inspectionService: InspectionService,
    private dialog: DialogService,
  ) {}

  ngOnInit() {
    // Debounce data loading
    this.loadDebounced
      .pipe(
        debounceTime(500),
        switchMap(() => this.loadDataLive()),
      )
      .subscribe();

    this.loadDataLive();
  }

  loadData() {
    this.loadDebounced.next();
  }

  async loadDataLive() {
    this.inspectionService
      .list({
        ...this.getParams(),
        status: InspectionStatus.ASSIGNMENT_MADE,
      })
      .then(result => {
        const plus2weeks = new Date();
        plus2weeks.setDate(plus2weeks.getDate() + 14);
        const inspections = result[0];
        this.noInspector = inspections.map(this.mapInspection).filter(i => !i.inspector);
        this.ready = inspections.map(this.mapInspection).filter(i => i.inspector);
      });

    this.inspectionService
      .list({
        ...this.getParams(),
        status: InspectionStatus.NEEDS_REVIEW,
      })
      .then(result => {
        this.pending = result[0].map(this.mapInspection);
      });

    this.inspectionService
      .list({
        ...this.getParams(),
        orderBy: 'inspection.rejected',
        status: InspectionStatus.REJECTED,
      })
      .then(result => {
        this.rejected = result[0].map(this.mapInspection);
      });
  }

  mapInspection(insp: Inspection) {
    return {
      ...insp,
      day: insp.dueDate ? dayAsString(new Date(insp.dueDate)) : '',
      month: insp.dueDate ? monthAsShortString(new Date(insp.dueDate)) : '',
      overdue: insp.dueDate && new Date(insp.dueDate).getTime() < new Date().getTime() ? true : false,
    };
  }

  getParams() {
    return {
      orderBy: 'inspection.dueDate',
      pageSize: 100,
    };
  }

  sendReminderRejected(ev, inspection: Inspection) {
    ev.stopPropagation();
    this.dialog.confirm().show({
      message: 'Send reminder to Inspector?',
      confirmed: async () => {
        await this.inspectionService.sendReminderToInspectorRejected(inspection.id);
        this.toastr.success('Message sent!');
      },
    });
  }

  sendReminderDue(ev, inspection: Inspection) {
    ev.stopPropagation();
    this.dialog.confirm().show({
      message: 'Send reminder to Inspector?',
      confirmed: async () => {
        await this.inspectionService.sendReminderToInspectorDue(inspection.id);
        this.toastr.success('Message sent!');
      },
    });
  }

  createReport(ev, inspection: Inspection) {
    this.dialog.confirm().show({
      header: 'Create inspection report',
      message: `The report will be generated and you'll receive a download link in your e-mail when it's ready`,
      confirmed: async () => {
        this.toastr.success('Your report is being generated');
        const reportParams = {
          applyFilters: false,
          showAdminComments: false,
          showFlags: false,
          showInspectorComments: true,
          showLegacyIds: false,
          showMarkupOverPictures: false,
          showNonMandatory: true,
          showQuestions: true,
        };
        await this.inspectionService.generateReport(inspection.id, {
          ...reportParams,
          filters: undefined,
        });
      },
    });
  }

  assignInspector(ev, inspection: Inspection) {
    ev.stopPropagation();
    this.assignInspectorModal.show({
      inspection,
      confirmed: () => {
        this.loadData();
      },
    });
  }

  getInspectionProgress(answers) {
    if (Array.isArray(answers)) {
      const reviewed = answers.filter(a => a.status !== AnswerStatus.NOT_REVIEWED);
      return (100 * reviewed.length) / answers.length;
    }
    return 0;
  }
}
